import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Clock, 
  FileText, 
  Home, 
  QrCode,
  Star,
  Bell,
  AlertCircle
} from 'lucide-react';
import Layout from '../../components/layout/Layout';
import './ReportSuccess.css';

const ReportSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    reportId, 
    binId, 
    issueType, 
    isDuplicate = false, 
    reportCount = 1, 
    priority = 'low' 
  } = location.state || {};

  useEffect(() => {
    // If no report data is available, redirect to scanner
    if (!reportId || !binId) {
      navigate('/scanner');
    }
  }, [reportId, binId, navigate]);

  const priorityDisplay = useMemo(() => {
    const priorityLabels = {
      low: 'Low Priority',
      medium: 'Medium Priority',
      high: 'High Priority',
      urgent: 'Urgent Priority'
    };

    return priorityLabels[priority] || priority;
  }, [priority]);

  const getIssueTypeDisplay = (type) => {
    const issueTypes = {
      overflow: 'Bin Overflow',
      damaged: 'Damaged Bin',
      missing: 'Missing Bin',
      blocked_access: 'Blocked Access',
      foul_smell: 'Foul Smell',
      pest_infestation: 'Pest Infestation',
      improper_waste: 'Improper Waste',
      maintenance_needed: 'Maintenance Needed',
      other: 'Other Issue'
    };
    return issueTypes[type] || type;
  };

  const nextSteps = useMemo(() => {
    if (isDuplicate) {
      return [
        {
          icon: <Bell size={20} />,
          title: 'Existing Report Updated',
          description: 'We added your confirmation to the active report and raised its priority.',
          status: 'pending'
        },
        {
          icon: <Clock size={20} />,
          title: 'Team Monitoring',
          description: 'Our team is already working on this bin. You\'ll receive updates as progress is made.',
          status: 'upcoming'
        },
        {
          icon: <FileText size={20} />,
          title: 'Stay Informed',
          description: 'Check the reports section to follow status changes and completion photos.',
          status: 'upcoming'
        }
      ];
    }

    return [
      {
        icon: <Bell size={20} />,
        title: 'Report Acknowledged',
        description: 'Our team will review your report within 2 hours',
        status: 'pending'
      },
      {
        icon: <FileText size={20} />,
        title: 'Issue Assessment',
        description: 'We\'ll assess the priority and assign appropriate resources',
        status: 'upcoming'
      },
      {
        icon: <Star size={20} />,
        title: 'Resolution & Feedback',
        description: 'Once resolved, you can rate our service and earn points',
        status: 'upcoming'
      }
    ];
  }, [isDuplicate]);

  if (!reportId || !binId) {
    return null; // Will redirect in useEffect
  }

  return (
    <Layout title="Report Submitted Successfully">
      <div className="report-success-container">
        {/* Success Header */}
        <div className="success-header">
          <div className="success-icon">
            <CheckCircle size={48} />
          </div>
          <h1>{isDuplicate ? 'Existing Report Updated!' : 'Report Submitted Successfully!'}</h1>
          <p>
            {isDuplicate
              ? 'Thanks for confirming the issue. Your report helped boost its priority.'
              : 'Thank you for helping keep our community clean.'}
          </p>
        </div>

        {/* Report Summary Card */}
        <div className="report-summary-card">
          <div className="summary-header">
            <h2>Report Summary</h2>
            <div className="report-id">#{reportId}</div>
          </div>
          
          <div className="summary-content">
            <div className="summary-item">
              <span className="label">Bin ID:</span>
              <span className="value">{binId}</span>
            </div>
            <div className="summary-item">
              <span className="label">Issue Type:</span>
              <span className="value">{getIssueTypeDisplay(issueType)}</span>
            </div>
            <div className="summary-item">
              <span className="label">Current Priority:</span>
              <span className={`status-badge priority-${priority}`}>
                <Clock size={14} />
                {priorityDisplay}
              </span>
            </div>
            <div className="summary-item">
              <span className="label">Total Reports:</span>
              <span className="value">{reportCount}</span>
            </div>
            <div className="summary-item">
              <span className="label">Submitted:</span>
              <span className="value">{new Date().toLocaleString()}</span>
            </div>
            <div className="summary-item">
              <span className="label">Status:</span>
              <span className="status-badge pending">
                <Clock size={14} />
                Pending Review
              </span>
            </div>
          </div>
        </div>

        {isDuplicate && (
          <div className="duplicate-info-card">
            <AlertCircle size={20} />
            <div>
              <h3>Bin Already Reported</h3>
              <p>
                We have linked your submission to the active report. Priority is now set to <strong>{priorityDisplay}</strong>
                {reportCount > 1 ? ` with ${reportCount} confirmations.` : '.'}
              </p>
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="next-steps-card">
          <h2>What Happens Next?</h2>
          <div className="steps-timeline">
            {nextSteps.map((step, index) => (
              <div key={index} className={`step-item ${step.status}`}>
                <div className="step-icon">
                  {step.icon}
                </div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                <div className="step-connector"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Points Earned */}
        {!isDuplicate && (
          <div className="points-card">
            <div className="points-icon">
              <Star size={24} />
            </div>
            <div className="points-content">
              <h3>+10 Points Earned!</h3>
              <p>Thank you for reporting this issue. Your contribution helps maintain clean communities.</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          <Link to="/reports" className="view-reports-btn">
            <FileText size={16} />
            View My Reports
          </Link>
          
          <Link to="/scanner" className="scan-another-btn">
            <QrCode size={16} />
            Scan Another Bin
          </Link>
          
          <Link to="/dashboard" className="dashboard-btn">
            <Home size={16} />
            Back to Dashboard
          </Link>
        </div>

        {/* Additional Info */}
        <div className="additional-info">
          <div className="info-card">
            <h3>Track Your Report</h3>
            <p>You can track the progress of your report in the "My Reports" section of your dashboard. We'll send you notifications when there are updates.</p>
          </div>
          
          <div className="info-card">
            <h3>Emergency Issues</h3>
            <p>For urgent health or safety concerns, please contact local authorities immediately. This reporting system is for non-emergency waste management issues.</p>
          </div>
        </div>

        {/* Share Achievement */}
        <div className="share-section">
          <h3>Share Your Impact</h3>
          <p>Help spread awareness about community cleanliness!</p>
          <div className="share-buttons">
            <button className="share-btn twitter">
              Share on Twitter
            </button>
            <button className="share-btn facebook">
              Share on Facebook
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ReportSuccess;
